import { useState } from 'react';
import toast from 'react-hot-toast';

import { Gate } from '../../../global/contexts/auth/auth';
import { IconScale, IconEdit, IconTrash } from '../../../common/icons';
import { fmtMoney } from '../../../common/utils/money';
import { formatDateOnly } from '../../../common/utils/date';
import { useConfirm } from '../../../common/components/ConfirmDialog/confirm';
import { useValuations, useDeleteValuation } from '../contexts/valuations';
import ValuationForm from '../forms/valuation-form';

import '../../../common/styles/catalog.css';

const BASIS_LABEL = { manual: 'Manual', market: 'Mercado', appraisal: 'Avalúo (perito)' };
const STATUS_LABEL = { 2: 'Vigente', 1: 'Invalidado', 0: 'Eliminado' };

// Valorización del animal (0098/0108): la vaca adulta se avalúa igual que una cría. Muestra el
// valor actual + historial con base, causa (narrativa de negocio) y estado, con avaluar/editar/
// borrar. GET kpis:read · mutaciones animals:write. Un avalúo explícito prela en el valor del inversor.
export default function AnimalValuationsPanel({ animal }) {
    const animalId = animal.id;
    const { data, isLoading, error } = useValuations('animals', animalId);
    const { mutateAsync: remove, isPending: removing } = useDeleteValuation('animals', animalId);
    const [form, setForm] = useState(null); // null | 'new' | valuation(edit)
    const confirm = useConfirm();

    const items = (data?.items || []).filter((v) => v.status !== 0);
    const latest = data?.latest;

    const del = async (v) => {
        const ok = await confirm({
            title: 'Eliminar avalúo',
            message: '¿Eliminar este avalúo?',
            confirmLabel: 'Eliminar',
            danger: true,
        });
        if (!ok) return;
        try { await remove(v.id); toast.success('Avalúo eliminado'); }
        catch (er) { if (er?.status !== 403) toast.error(er?.message || 'No se pudo eliminar'); }
    };

    return (
        <section className="cat-detail__card">
            <div className="cat-detail__section-head">
                <h2 className="cat-detail__section-title">
                    Valorización
                    {latest && <span className="cat-detail__count">{fmtMoney(latest.estimated_value)}</span>}
                </h2>
                <Gate domain="animals" action="write">
                    <button type="button" className="cat-detail__edit" onClick={() => setForm('new')}><IconScale /><span>Avaluar</span></button>
                </Gate>
            </div>

            {error ? (
                <p className="cat-detail__empty">{error.status === 403 ? 'Sin permiso para ver la valorización.' : 'No se pudo cargar la valorización.'}</p>
            ) : isLoading ? (
                <p className="cat-detail__empty">Cargando…</p>
            ) : !items.length ? (
                <p className="cat-detail__empty">Este animal aún no tiene avalúos.</p>
            ) : (
                <table className="cat-table cat-table--compact">
                    <thead>
                        <tr><th>Fecha</th><th>Valor</th><th>Base</th><th>Causa</th><th>Estado</th><th className="cat-table__actions-col" aria-label="Acciones" /></tr>
                    </thead>
                    <tbody>
                        {items.map((v) => (
                            <tr key={v.id} className="cat-table__row">
                                <td>{formatDateOnly(v.valuation_date)}</td>
                                <td className="cat-table__name">{fmtMoney(v.estimated_value)}</td>
                                <td>{BASIS_LABEL[v.basis] || v.basis}</td>
                                <td>{v.cause || <span className="cat-table__empty">—</span>}</td>
                                <td><span className={`cat-badge cat-badge--${v.status}`}>{STATUS_LABEL[v.status] || v.status}</span></td>
                                <td className="cat-table__actions">
                                    <Gate domain="animals" action="write">
                                        <button type="button" className="cat-table__edit" onClick={() => setForm(v)} disabled={removing} aria-label="Editar avalúo" title="Editar"><IconEdit /></button>
                                        <button type="button" className="cat-table__edit" onClick={() => del(v)} disabled={removing} aria-label="Eliminar avalúo" title="Eliminar"><IconTrash /></button>
                                    </Gate>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {form && <ValuationForm subject={{ kind: 'animals', id: animal.id, label: animal.name || animal.number || `#${animal.id}` }} valuation={form === 'new' ? null : form} onClose={() => setForm(null)} onSaved={() => setForm(null)} />}
        </section>
    );
}
